import { Injectable, inject, signal } from '@angular/core';
import { BizEntityTypeCategory, IBizEntity, IBizEntityType, LoggerService } from '@optima-stride-ai/core';
import {
  BizEntityStateStoreService,
  BizEntityTypeStateStoreService,
  SystemDataCreationService,
} from '@optima-stride-ai/optima-app-framework';
import { FileOperationsService } from '../pages/libraries/services/file-operations.service';

/** Map from fileTypeCounts key → BizEntityTypeCategory used by handleNewFileCreation */
const FILE_TYPE_CATEGORY: Record<string, BizEntityTypeCategory> = {
  presentation: BizEntityTypeCategory.HayagrivaFilePresentation,
  markdown: BizEntityTypeCategory.HayagrivaFileMarkdown,
  doc: BizEntityTypeCategory.HayagrivaFileDoc,
  spreadsheet: BizEntityTypeCategory.HayagrivaFileSpreadsheet,
  drawing: BizEntityTypeCategory.HayagrivaFileDrawing,
  csv: BizEntityTypeCategory.HayagrivaFileCSV,
};

/**
 * File-type counts per folder. The seed structure (Libraries + Folders) is
 * fixed by the framework's HAYAGRIVA_ENTITY_GENERATION_DEFS — only file
 * counts are user-configurable here.
 */
export interface HayagrivaGenConfig {
  fileTypeCounts: {
    presentation: number;
    markdown: number;
    doc: number;
    spreadsheet: number;
    drawing: number;
    [key: string]: number;
  };
}

/** A named preset shown in the data gen panel pack selector */
export interface HayagrivaDataPack {
  id: string;
  displayName: string;
  description: string;
  defaultConfig: HayagrivaGenConfig;
  /** When true the panel shows editable count inputs; when false it uses defaultConfig as-is */
  configurable: boolean;
}

export const HAYAGRIVA_DATA_PACKS: HayagrivaDataPack[] = [
  {
    id: 'smoke',
    displayName: 'Smoke',
    description: '1 of each file type per folder — quick sanity check',
    defaultConfig: {
      fileTypeCounts: { presentation: 1, markdown: 1, doc: 1, spreadsheet: 1, drawing: 1 },
    },
    configurable: false,
  },
  {
    id: 'dense',
    displayName: 'Dense',
    description: 'Custom file counts per folder — configure below',
    defaultConfig: {
      fileTypeCounts: { presentation: 2, markdown: 2, doc: 3, spreadsheet: 2, drawing: 1 },
    },
    configurable: true,
  },
];

@Injectable({ providedIn: 'root' })
export class HayagrivaDataGenService {
  private readonly dataCreation = inject(SystemDataCreationService);
  private readonly fileOps = inject(FileOperationsService);
  private readonly bizEntityStore = inject(BizEntityStateStoreService);
  private readonly bizEntityTypeStore = inject(BizEntityTypeStateStoreService);
  private readonly logger = inject(LoggerService);

  readonly logs = signal<string[]>([]);

  private log(msg: string): void {
    this.logger.info(msg);
    this.logs.update((l) => [...l, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  }

  async seedOnly(skipUsersFromAdminTeam: string[] = []): Promise<{ success: boolean; error?: string }> {
    this.logs.set([]);
    try {
      this.log('Seeding structure — deleting existing data and creating Libraries + Folders…');
      this.dataCreation.setConfig({ skipUsersFromAdminTeam });
      const result = await this.dataCreation.cleanAndCreateTestData();
      if (!result.success) {
        throw new Error(result.error ?? 'Seed failed');
      }
      this.log('✅ Structure seeded successfully');
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.log(`❌ Error: ${msg}`);
      return { success: false, error: msg };
    }
  }

  async generateAll(
    config: HayagrivaGenConfig,
    skipUsersFromAdminTeam: string[] = [],
  ): Promise<{ success: boolean; filesCreated: number; filesFailed: number; error?: string }> {
    this.logs.set([]);

    try {
      // ── Phase 1: Seed ──────────────────────────────────────────────────────────────
      // Creates Libraries + Library Folders as BizEntities via the framework
      this.log('Phase 1 — Creating seed data (Libraries + Folders)…');
      this.dataCreation.setConfig({ skipUsersFromAdminTeam });
      const seedResult = await this.dataCreation.cleanAndCreateTestData();
      if (!seedResult.success) {
        throw new Error(seedResult.error ?? 'Seed phase failed');
      }
      this.log('✅ Seed complete');

      // ── Phase 2: Resolve entity types ────────────────────────────────────
      this.log('Phase 2 — Resolving entity types…');

      // Phase 3 also fetches all entities, so we get both in one batch call here.
      // getAllEntities + getState share the same call — we do ONE getState for types
      // and ONE getState for entities. No per-library / per-folder API calls needed.
      const [entityTypes, allEntities] = await Promise.all([
        this.bizEntityTypeStore.getState().then((r) => (r ?? []) as IBizEntityType[]),
        this.bizEntityStore.getState(this.bizEntityTypeStore).then((r) => (r ?? []) as IBizEntity[]),
      ]);

      const typeByCategory = new Map<string, IBizEntityType>();
      for (const et of entityTypes) {
        typeByCategory.set(String(et.category), et);
      }

      // ── Phase 3: Find app root, Libraries and Folders — all in memory ────
      // All entities are already loaded above; filter by category + parentId
      // instead of making further API calls per library/folder.
      this.log('Phase 3 — Resolving app root, libraries and folders from loaded entity tree…');

      const appRootEntity = allEntities.find(
        (e) => e.type?.category === BizEntityTypeCategory.ApplicationRootNode,
      );
      if (!appRootEntity) {
        throw new Error('ApplicationRootNode BizEntity not found — ensure seed data has been created');
      }
      this.log(`App root: "${appRootEntity.name}" (id: ${appRootEntity.id})`);

      // Use loose equality to handle numeric vs. string parentId from the API
      const libraries = allEntities.filter(
        // eslint-disable-next-line eqeqeq
        (e) => e.type?.category === BizEntityTypeCategory.HayagrivaLibrary && e.parentId == appRootEntity.id,
      );
      this.log(`Found ${libraries.length} librar${libraries.length === 1 ? 'y' : 'ies'}`);

      // ── Phase 4: Create files in each Folder ──────────────────────────────
      this.log('Phase 4 — Creating operational file entities via FileOperationsService…');
      let filesCreated = 0;
      let filesFailed = 0;

      for (const lib of libraries) {
        // eslint-disable-next-line eqeqeq
        const folders = allEntities.filter(
          // eslint-disable-next-line eqeqeq
          (e) => e.type?.category === BizEntityTypeCategory.HayagrivaLibFolder && e.parentId == lib.id,
        );
        this.log(`  Library "${lib.name}": ${folders.length} folder(s)`);

        for (const folder of folders) {
          for (const [fileTypeKey, count] of Object.entries(config.fileTypeCounts)) {
            if (count <= 0) continue;

            const category = FILE_TYPE_CATEGORY[fileTypeKey];
            if (!category) {
              this.log(`  ⚠️  Unknown file type key "${fileTypeKey}" — skipping`);
              continue;
            }

            const entityType = typeByCategory.get(String(category));
            if (!entityType) {
              this.log(`  ⚠️  No entity type found for "${fileTypeKey}" (category ${category}) — skipping`);
              continue;
            }

            for (let i = 1; i <= count; i++) {
              const fileName = `${fileTypeKey}-${i}`;
              try {
                await this.fileOps.handleNewFileCreation(entityType, fileName, folder.id);
                filesCreated++;
                this.log(`  ✅ Created ${fileTypeKey} "${fileName}" in folder "${folder.name}"`);
              } catch (err) {
                filesFailed++;
                this.log(`  ❌ Failed to create ${fileTypeKey} "${fileName}": ${err}`);
              }
            }
          }
        }
      }

      this.log(
        `Done — files created: ${filesCreated}, failed: ${filesFailed}`,
      );
      return { success: filesFailed === 0, filesCreated, filesFailed };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.log(`❌ Fatal error: ${msg}`);
      return { success: false, filesCreated: 0, filesFailed: 0, error: msg };
    }
  }
}
